// PRO Drift Auditor -- underwater (drawdown) comparison: Unchecked Drift vs
// Static Corridor vs AI Regime PRO. Lower is worse; the AI curve should sit
// shallower during stress.

import React from 'react';
import {
    Area,
    AreaChart,
    CartesianGrid,
    Legend,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';
import { TrendingDown } from 'lucide-react';

import { Card } from './Card';
import type { ProTimeSeriesPoint } from '../types';

export const DrawdownRegimeChart: React.FC<{ data: ProTimeSeriesPoint[] }> = ({ data }) => (
    <Card
        title="Drawdown Defense"
        subtitle="Peak-to-trough underwater curves. The Forge Ultra PRO line should stay shallower in stress."
        icon={<TrendingDown className="h-4 w-4" />}
    >
        <div className="h-72 w-full">
            <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={data} margin={{ top: 6, right: 10, left: 6, bottom: 0 }}>
                    <defs>
                        <linearGradient id="ddDrift" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#f43f5e" stopOpacity={0.35} />
                            <stop offset="100%" stopColor="#f43f5e" stopOpacity={0.02} />
                        </linearGradient>
                        <linearGradient id="ddPro" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#10b981" stopOpacity={0.35} />
                            <stop offset="100%" stopColor="#10b981" stopOpacity={0.02} />
                        </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" strokeOpacity={0.4} vertical={false} />
                    <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#94a3b8' }} minTickGap={56} tickLine={false} />
                    <YAxis
                        tickFormatter={(v: number) => `${v.toFixed(0)}%`}
                        tick={{ fontSize: 11, fill: '#94a3b8' }}
                        width={44}
                        tickLine={false}
                        axisLine={false}
                        domain={['auto', 0]}
                    />
                    <Tooltip
                        formatter={(v: number, name) => [`${v.toFixed(2)}%`, name]}
                        contentStyle={{
                            borderRadius: 12,
                            border: '1px solid #e2e8f0',
                            fontSize: 12,
                            background: 'rgba(255,255,255,0.97)',
                        }}
                    />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Area
                        type="monotone"
                        dataKey="drifted_drawdown_pct"
                        name="Unchecked Drift"
                        stroke="#f43f5e"
                        strokeWidth={1.75}
                        fill="url(#ddDrift)"
                    />
                    <Area
                        type="monotone"
                        dataKey="corridor_drawdown_pct"
                        name="Static Corridor"
                        stroke="#6366f1"
                        strokeWidth={1.5}
                        fillOpacity={0}
                    />
                    <Area
                        type="monotone"
                        dataKey="ai_pro_drawdown_pct"
                        name="Forge Ultra Regime PRO"
                        stroke="#10b981"
                        strokeWidth={2.25}
                        fill="url(#ddPro)"
                    />
                </AreaChart>
            </ResponsiveContainer>
        </div>
    </Card>
);
